import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Flame, ArrowRight, CheckCircle2, Users, Trophy, HeartHandshake, CalendarDays, Salad, Dumbbell, PlayCircle } from 'lucide-react';
import { videosAPI, mediaUrl } from '../services/api';

const secrets = [
  { n: 1, days: '1-4', title: 'Équilibre calories / dépenses', icon: Salad },
  { n: 2, days: '5-8', title: 'Les protéines, moteur du métabolisme', icon: Dumbbell },
  { n: 3, days: '9-12', title: 'Le petit-déjeuner, clé de l’énergie', icon: Flame },
  { n: 4, days: '13-16', title: 'Les bons glucides et l’index glycémique', icon: Salad },
  { n: 5, days: '17-21', title: 'Les bonnes graisses', icon: HeartHandshake },
];

const steps = [
  { icon: CalendarDays, title: 'Un défi chaque jour', text: 'Mouvement + nutrition, en version débutant ou avancé.' },
  { icon: CheckCircle2, title: 'Tu valides', text: 'Photo, vidéo ou commentaire. Chaque petite action compte.' },
  { icon: Trophy, title: 'Tu gagnes', text: 'Points, badges et séries pour rester motivé(e).' },
  { icon: Users, title: 'Tu partages', text: 'Une communauté bienveillante qui t’encourage.' },
];

export default function Landing() {
  const [landingVideo, setLandingVideo] = useState(null);
  useEffect(() => {
    videosAPI.landing().then((v) => setLandingVideo(v?.url ? v : null)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 bfc-dark backdrop-blur-md border-b border-neutral-800">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/bfc21-logo.png" alt="Body Fit Challenge 21" className="w-10 h-10 rounded-full" />
            <span className="font-extrabold tracking-tight text-white hidden sm:block">BODY FIT <span className="text-red-500">CHALLENGE 21</span></span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" data-testid="landing-login-btn" className="text-white hover:bg-neutral-800 font-semibold">Connexion</Button></Link>
            <Link to="/register"><Button data-testid="landing-register-btn" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full">Je m’inscris</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bfc-hero-glow" />
        <div className="relative max-w-6xl mx-auto px-4 pt-14 pb-16 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-rise">
            <Badge className="mb-5 bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-100 rounded-full px-3 py-1">
              Challenge ludique de 21 jours
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-red-950">
              Reprends le contrôle,
              <span className="block bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">retrouve ton énergie</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              21 jours pour installer de nouvelles habitudes, sans régime extrême. Nutrition, mouvement,
              motivation et entraide — un défi bienveillant à la fois.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button size="lg" data-testid="hero-cta-btn" className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-7 group w-full sm:w-auto">
                  Rejoindre le challenge gratuitement
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full border-red-300 text-red-800 font-semibold w-full sm:w-auto">
                  J’ai déjà un compte
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-red-600" /> Sans régime extrême</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-red-600" /> Débutant ou avancé</span>
            </div>
          </div>

          <div className="relative animate-rise" style={{ animationDelay: '120ms' }}>
            <img
              src="https://static.prod-images.emergentagent.com/jobs/1e4341af-149d-4006-aee2-43afbb17ceae/images/de53e23c5574df82e38248df8671f401aaf54ee2644434cfa203e25751cfd229.jpeg"
              alt="Membres BFC21 actifs et souriants au lever du soleil"
              className="rounded-3xl shadow-xl object-cover w-full h-[420px]"
            />
            <div className="absolute -bottom-5 -left-4 bg-white rounded-2xl shadow-lg p-4 flex items-center gap-3 border border-red-100">
              <div className="w-11 h-11 rounded-xl bfc-gradient-warm flex items-center justify-center">
                <Flame className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Ta série</p>
                <p className="font-extrabold text-red-950">Jour 5 · 🔥 5 jours</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Presentation video */}
      <section className="max-w-4xl mx-auto px-4 pt-6 pb-4">
        <div className="text-center mb-6">
          <Badge className="mb-3 bg-red-100 text-red-700 border-red-200 hover:bg-red-100 rounded-full">Découvre le challenge en vidéo</Badge>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-neutral-900">Body Fit Challenge 21, c'est quoi ?</h2>
        </div>
        <div className="rounded-3xl overflow-hidden shadow-xl bg-black" data-testid="landing-video">
          {landingVideo ? (
            <video src={mediaUrl(landingVideo.url)} controls playsInline className="w-full aspect-video object-cover bg-black" />
          ) : (
            <div className="w-full aspect-video flex flex-col items-center justify-center bfc-dark text-white gap-3">
              <PlayCircle className="w-16 h-16 text-red-500" />
              <p className="text-sm text-neutral-300">La vidéo de présentation arrive bientôt</p>
            </div>
          )}
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-6xl mx-auto px-4 py-14">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-red-950 text-center">Comment ça marche ?</h2>
        <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-red-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-4">
                <s.icon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="font-bold text-red-950 mb-1">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Secrets */}
      <section className="bg-red-50/60 py-14">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-red-950 text-center">Les 5 secrets BFC21</h2>
          <p className="text-center text-slate-600 mt-2">Chaque secret guide une phase de ton challenge</p>
          <div className="mt-9 space-y-3">
            {secrets.map((s) => (
              <div key={s.n} className="bg-white rounded-2xl p-5 border border-red-100 flex items-center gap-4 shadow-sm">
                <div className="w-12 h-12 rounded-xl bfc-gradient flex items-center justify-center text-white font-extrabold shrink-0">{s.n}</div>
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-red-700/80">Jours {s.days}</p>
                  <p className="font-bold text-red-950">{s.title}</p>
                </div>
                <s.icon className="w-6 h-6 text-red-400 hidden sm:block" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 py-16 text-center">
        <div className="bfc-gradient rounded-3xl p-10 sm:p-14 text-white relative overflow-hidden">
          <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">Prêt(e) à relever le défi ?</h2>
          <p className="mt-3 text-red-50 max-w-xl mx-auto">Rejoins une communauté motivante et transforme tes habitudes en 21 jours.</p>
          <Link to="/register">
            <Button size="lg" data-testid="footer-cta-btn" className="mt-7 bg-white text-red-700 hover:bg-red-50 font-bold rounded-full px-8">
              Commencer maintenant
            </Button>
          </Link>
        </div>
        <p className="mt-8 text-xs text-slate-400 max-w-2xl mx-auto leading-relaxed">
          Ce challenge est un programme d’accompagnement bien-être et habitudes de vie. Il ne remplace pas un avis médical.
          En cas de problème de santé, demandez conseil à un professionnel.
        </p>
      </section>
    </div>
  );
}
